import { color, isWeb, StackProps, styled, XStack, YStack } from '@mythical/ui'
import { KeyboardAwareScrollView } from 'app/components/primitive/KeyboardAwareScrollView'
import React, { useState } from 'react'
import { RefreshControlProps, StatusBar, StyleSheet } from 'react-native'
import { observer } from 'mobx-react-lite'
import {
  PromoBanner,
  PromoBannerProps,
} from 'app/components/primitive/PromoBanner'
import { UpgradeFlowModals } from 'app/components/structure/Layout/UpgradeFlowModals'
import Footer from 'app/components/structure/Footer'
import Navigation, {
  type MainNavigationProps,
} from 'app/components/structure/Navigation/MainNavigation'
import Sidebar from 'app/components/structure/Navigation/Sidebar'
import { NAV_HEIGHT } from 'app/components/structure/Navigation/constants'
import { useSafeArea } from 'app/provider/safe-area/use-safe-area'

interface LayoutProps {
  noSearch?: boolean
  noHeader?: boolean
  noScroll?: boolean
  noSidebar?: boolean
  noFooter?: boolean
  showBackBtn?: boolean
  refreshControl?: React.ReactElement<RefreshControlProps>
  contentStyles?: StackProps
  children: React.ReactNode
  scrollRef?: React.RefObject<KeyboardAwareScrollView>
  promoBannerProps?: PromoBannerProps
  paddedTop?: boolean
  paddedBottom?: boolean
  headerProps?: MainNavigationProps
}
interface ContentWithScrollProps {
  children: React.ReactNode
  refreshControl?: React.ReactElement<RefreshControlProps>
  contentStyles?: StackProps
  noFooter?: boolean
  showBackBtn?: boolean
  scrollRef?: React.RefObject<KeyboardAwareScrollView>
  paddedTop?: boolean
  paddedBottom?: boolean
}
interface ContentProps {
  contentStyles?: StackProps
  showBackBtn?: boolean
  children: React.ReactNode
  paddedTop?: boolean
  paddedBottom?: boolean
}

const styles = StyleSheet.create({
  contentContainer: {
    flexGrow: 1,
    width: '100%',
  },
})

const ContentWrapper = styled(YStack, {
  w: '100%',
  flexGrow: 1,
  mx: 'auto',
  maxWidth: 1440,
  variants: {
    paddedTop: {
      true: {
        pt: '$3.5',
        $gtMd: {
          pt: '$6',
        },
      },
    },
    paddedBottom: {
      true: {
        pb: '$7',
        $gtMd: {
          pb: '$3.5',
        },
      },
      false: {
        pb: '$0',
      },
    },
  } as const,
})

const Content: React.FC<ContentProps> = ({
  children,
  contentStyles,
  paddedTop,
  paddedBottom,
}) => {
  return (
    <ContentWrapper
      paddedTop={paddedTop}
      paddedBottom={paddedBottom}
      {...contentStyles}
    >
      {children}
    </ContentWrapper>
  )
}

const ContentWithoutScroll: React.FC<ContentWithScrollProps> = ({
  children,
  contentStyles,
  noFooter,
  paddedTop,
  paddedBottom,
}) => {
  return (
    <YStack flexGrow={1} flexShrink={1} width="100%">
      <Content
        contentStyles={contentStyles}
        paddedTop={paddedTop}
        paddedBottom={paddedBottom}
      >
        {children}
      </Content>
      {!noFooter && <Footer />}
    </YStack>
  )
}

const ContentWithScroll: React.FC<ContentWithScrollProps> = ({
  children,
  refreshControl,
  contentStyles,
  noFooter,
  scrollRef,
  paddedTop,
  paddedBottom,
}) => {
  return (
    <KeyboardAwareScrollView
      enableOnAndroid
      extraScrollHeight={100}
      contentContainerStyle={styles.contentContainer}
      bounces
      refreshControl={refreshControl}
      nestedScrollEnabled
      ref={scrollRef}
      nativeID="scroll-container"
    >
      <Content
        contentStyles={contentStyles}
        paddedTop={paddedTop}
        paddedBottom={paddedBottom}
      >
        {children}
      </Content>
      {!noFooter && <Footer />}
    </KeyboardAwareScrollView>
  )
}

const MainLayout: React.FC<LayoutProps> = observer(
  ({
    children,
    refreshControl,
    contentStyles,
    showBackBtn,
    noSidebar,
    noFooter = true,
    noSearch,
    noScroll,
    noHeader,
    headerProps,
    scrollRef,
    promoBannerProps,
    // Standard padding for all pages
    paddedTop = true,
    paddedBottom = true,
  }) => {
    const insets = useSafeArea()
    const ContentComp = noScroll ? ContentWithoutScroll : ContentWithScroll
    const [displayPromo, setDisplayPromo] = useState(true)

    return (
      <YStack
        zIndex={0}
        bg="$bgPrimary"
        fs={1}
        fg={1}
        // Compensate for insets + nav
        {...(!isWeb && { pt: insets.top + NAV_HEIGHT })}
      >
        <StatusBar backgroundColor={color.bgPrimary} barStyle="light-content" />

        {!noHeader && isWeb && (
          <Navigation
            showBackWeb={showBackBtn}
            hideSearch={noSearch}
            {...headerProps}
          />
        )}

        <YStack
          height="100%"
          pt={isWeb ? NAV_HEIGHT : '$0'}
          {...(isWeb && { minHeight: '100dvh' })}
          $gtMd={{
            ...(isWeb && {
              maxHeight: `calc(100dvh - ${NAV_HEIGHT}px)`,
              overflowY: 'hidden',
            }),
          }}
        >
          {displayPromo && promoBannerProps?.text && (
            <PromoBanner
              onClose={() => setDisplayPromo(false)}
              {...promoBannerProps}
            />
          )}

          <XStack flex={1} h="100%">
            {!noSidebar && <Sidebar />}

            <ContentComp
              refreshControl={refreshControl}
              contentStyles={contentStyles}
              showBackBtn={showBackBtn}
              noFooter={noFooter}
              scrollRef={scrollRef}
              paddedTop={paddedTop}
              paddedBottom={paddedBottom}
            >
              {children}
            </ContentComp>
          </XStack>
        </YStack>

        {isWeb && <UpgradeFlowModals />}
      </YStack>
    )
  },
)

export default MainLayout
